import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContainer,
  Heading,
  Content,
  Flex,
  TextField,
  View,
  Link,
  Text,
} from '@adobe/react-spectrum';
import { BarcodeFormat, BarcodeScanner } from 'react-barcode-scanner';
import 'react-barcode-scanner/polyfill';
import { useTranslation } from 'react-i18next';
import styles from './BillingLineComponent.module.css';

interface BarcodeScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (barcode: string) => void;
  isMobile: boolean;
}

const BarcodeScannerModal: React.FC<BarcodeScannerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  isMobile,
}) => {
  const { t } = useTranslation();
  const [scannedValue, setScannedValue] = useState<string>('');
  const [useCameraScanner, setUseCameraScanner] = useState<boolean>(true);
  const [cameraPermission, setCameraPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [permissionError, setPermissionError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      checkCameraPermission();
    } else {
      // Reset state when modal closes
      setScannedValue('');
      setUseCameraScanner(true);
      setPermissionError('');
    }
  }, [isOpen]);

  const checkCameraPermission = async () => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
        setCameraPermission(permissionStatus.state as 'granted' | 'denied' | 'prompt');
        
        permissionStatus.onchange = () => {
          setCameraPermission(permissionStatus.state as 'granted' | 'denied' | 'prompt');
        };
      }
    } catch (error) {
      console.warn('Camera permission check not supported:', error);
    }
  };

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraPermission('granted');
      setPermissionError('');
      // Stop the stream immediately as we just needed to request permission
      stream.getTracks().forEach(track => track.stop());
    } catch (error: any) {
      setCameraPermission('denied');
      setPermissionError(t('camera_permission_denied', { ns: 'layout_components' }));
      console.error('Camera permission denied:', error);
    }
  };

  const handleBarcodeDetected = (barcodes: any[]) => {
    if (barcodes && barcodes.length > 0) {
      const barcode = barcodes[0];
      const barcodeValue = barcode.rawValue || barcode.data || barcode.text || '';
      if (barcodeValue && barcodeValue.trim()) {
        setScannedValue(barcodeValue.trim());
      }
    }
  };

  const handleSelect = () => {
    if (scannedValue.trim()) {
      onSelect(scannedValue.trim());
    }
  };

  const handleClose = () => {
    setScannedValue('');
    setUseCameraScanner(true);
    setPermissionError('');
    onClose();
  };

  const renderCameraScanner = () => {
    if (cameraPermission === 'denied') {
      return (
        <View
          padding="size-200"
          backgroundColor="gray-200"
          borderRadius="medium"
          minHeight="200px"
          UNSAFE_style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Text>{permissionError || t('camera_permission_denied', { ns: 'layout_components' })}</Text>
        </View>
      );
    }

    if (cameraPermission === 'prompt') {
      return (
        <View
          padding="size-200"
          backgroundColor="gray-200"
          borderRadius="medium"
          minHeight="200px"
          UNSAFE_style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}
        >
          <Text>{t('camera_permission_required', { ns: 'layout_components' })}</Text>
          <Link onPress={requestCameraPermission} UNSAFE_className={styles.btn_confirm}>
            {t('allow_camera', { ns: 'layout_components' })}
          </Link>
        </View>
      );
    }

    return (
      <View
        borderRadius="medium"
        overflow="hidden"
        UNSAFE_style={{ width: '100%', maxWidth: '500px', margin: '0 auto' }}
      >
        <BarcodeScanner
          onCapture={handleBarcodeDetected}
          options={{
            formats: [
              BarcodeFormat.CODE_128,
              BarcodeFormat.CODE_39,
              BarcodeFormat.CODE_93,
              BarcodeFormat.EAN_13,
              BarcodeFormat.EAN_8,
              BarcodeFormat.ITF,
              BarcodeFormat.QR_CODE,
              BarcodeFormat.UPC_A,
              BarcodeFormat.UPC_E
            ],
          }}
        />
      </View>
    );
  };

  return (
    <DialogContainer onDismiss={handleClose}>
      {isOpen && (
        <Dialog size={isMobile ? 'S' : 'L'} isDismissable>
          <Heading>{t('barcode_scanner', { ns: 'layout_components' })}</Heading>
          <Content>
            <Flex direction="column" gap="size-200" width="100%">
              {/* Toggle between camera and manual entry */}
              <Flex direction="row" gap="size-100" justifyContent="center">
                <Link
                  isQuiet={!useCameraScanner}
                  onPress={() => setUseCameraScanner(true)}
                  UNSAFE_style={{
                    fontWeight: useCameraScanner ? 'bold' : 'normal',
                    color: useCameraScanner ? '#1976d2' : '#595959',
                  }}
                >
                  {t('scan_with_camera', { ns: 'layout_components' })}
                </Link>
                <Text>|</Text>
                <Link
                  isQuiet={useCameraScanner}
                  onPress={() => setUseCameraScanner(false)}
                  UNSAFE_style={{
                    fontWeight: !useCameraScanner ? 'bold' : 'normal',
                    color: !useCameraScanner ? '#1976d2' : '#595959',
                  }}
                >
                  {t('manual_entry', { ns: 'layout_components' })}
                </Link>
              </Flex>

              {/* Camera Scanner or Manual Entry */}
              {useCameraScanner ? (
                <View>
                  {renderCameraScanner()}
                </View>
              ) : (
                <View>
                  <Text UNSAFE_style={{ marginBottom: '8px', fontSize: '14px', color: '#455a64' }}>
                    {t('manual_entry_instruction', { ns: 'layout_components' })}
                  </Text>
                </View>
              )}

              {/* Scanned/Entered Barcode Display */}
              <TextField
                label={t('scanned_barcode', { ns: 'layout_components' })}
                value={scannedValue}
                onChange={setScannedValue}
                width="100%"
                isRequired
                description={t('enter_barcode_manually', { ns: 'layout_components' })}
              />

              {/* Action Buttons */}
              <Flex
                direction="row"
                gap="size-200"
                justifyContent="end"
                marginTop="size-200"
              >
                <Link UNSAFE_className={styles.btn_cancel} isQuiet onPress={handleClose}>
                  {t('close', { ns: 'layout_components' })}
                </Link>
                <Link
                  UNSAFE_className={styles.btn_confirm}
                  isQuiet
                  onPress={scannedValue.trim() ? handleSelect : undefined}
                  UNSAFE_style={{
                    opacity: scannedValue.trim() ? 1 : 0.5,
                    cursor: scannedValue.trim() ? 'pointer' : 'not-allowed',
                  }}
                >
                  {t('select', { ns: 'layout_components' })}
                </Link>
              </Flex>
            </Flex>
          </Content>
        </Dialog>
      )}
    </DialogContainer>
  );
};

export default BarcodeScannerModal;

